import { useEffect, useState } from "react";
import {
  Messages,
  NotificationSeverity,
  TABLE_ACTIONS,
} from "helpers/constants";
import ConfirmActionModal from "modals/ConfirmActionModal/ConfirmActionModal";
import CustomTable from "components/Table/CustomTable";
import { Tag } from "types/tag";
import {
  useDeleteTagMutation,
  useGetTagsQuery,
} from "graphql/__generated__/hooks";
import CreateOrEditTagModal from "modals/CreateOrEditTagModal/CreateOrEditTagModal";
import { MainLayoutContainer } from "components/Layout";
import { Add } from "@mui/icons-material";
import { ResponsiveButton } from "components/Button";
import { useNotifications } from "hooks/useNotifications";

export default function TagsList() {
  const [createTagModal, setCreateTagModal] = useState(false);
  const [deleteTagModal, setDeleteTagModal] = useState(false);
  const [tagIdToDelete, setTagIdToDelete] = useState("");
  const [tags, setTags] = useState([] as Tag[]);
  const [selectedTag, setSelectedTag] = useState<Tag | null>(null);
  const [deleteTag] = useDeleteTagMutation();
  const { loading, error, data, refetch } = useGetTagsQuery();
  const { notify } = useNotifications();

  useEffect(() => {
    setTags((data?.tag as Tag[]) || []);
  }, [data]);

  const handleActionClick = (action: TABLE_ACTIONS, id: string) => {
    switch (action) {
      case TABLE_ACTIONS.EDIT:
        const tagToEdit = tags?.find((tag) => tag.tag_id === id);
        if (tagToEdit) {
          setSelectedTag(tagToEdit);
          setCreateTagModal(true);
        }
        break;
      case TABLE_ACTIONS.DELETE:
        setTagIdToDelete(id);
        setDeleteTagModal(true);
        break;
      default:
        break;
    }
  };

  const handleCreateTagModalOpen = () => {
    setCreateTagModal(true);
  };

  const handleCreateOrEditTagChange = () => {
    if (selectedTag !== null) {
      setSelectedTag(null);
    }
    setCreateTagModal(false);
    refetch().then();
  };

  const handleDelete = async () => {
    if (!tagIdToDelete || tagIdToDelete === "") {
      return;
    }
    await deleteTag({ variables: { id: tagIdToDelete } });
    await refetch();
    setTagIdToDelete("");
    notify({
      message: Messages.successTagDeleted,
      severity: NotificationSeverity.success,
    });
  };

  return (
    <MainLayoutContainer
      title="Tags"
      loading={loading}
      error={error}
      buttonActions={
        <ResponsiveButton
          onClick={() => handleCreateTagModalOpen()}
          icon={<Add />}
        >
          New Tag
        </ResponsiveButton>
      }
    >
      <CustomTable
        entities={tags}
        handleActionClick={handleActionClick}
        fieldToFilter={"name"}
        columns={[
          {
            id: "name",
            label: "Name",
            value: "name",
          },
          {
            id: "type",
            label: "Type",
            value: "type",
          },
          { id: "actions", value: "" },
        ]}
        isSelectable={false}
        allowedMenuItems={[TABLE_ACTIONS.EDIT, TABLE_ACTIONS.DELETE]}
        extraActionItems={[]}
        idColumn={"tag_id"}
      />

      {createTagModal && (
        <CreateOrEditTagModal
          handleCreateOrEditTagChange={handleCreateOrEditTagChange}
          createOrEditTagModal={createTagModal}
          handleCreateOrEditTagModalDismiss={() => {
            setCreateTagModal(false);
            if (selectedTag !== null) {
              setSelectedTag(null);
            }
          }}
          tag={selectedTag}
        />
      )}

      {deleteTagModal && (
        <ConfirmActionModal
          title={"Heads up!"}
          message={"Are you sure you want to delete this tag?"}
          show={deleteTagModal}
          onConfirm={handleDelete}
          onDismiss={() => {
            setDeleteTagModal(false);
          }}
        />
      )}
    </MainLayoutContainer>
  );
}
